import { Badge, Group, Stack, Switch, Table, Text, Title, Tooltip } from '@mantine/core';
import { useOcrProviders, usePatchOcrProvider } from '../../queries/ocr';
import { useAuthStore } from '../../auth/auth-store';

export function OcrProvidersTable() {
  const { data: providers = [], isLoading } = useOcrProviders();
  const patch = usePatchOcrProvider();
  const me = useAuthStore((s) => s.user);
  const isSuperadmin = me?.system_role === 'superadmin';

  return (
    <Stack gap="xs">
      <Group justify="space-between" align="flex-end">
        <Stack gap={0}>
          <Title order={4}>OCR providers</Title>
          <Text c="dimmed" size="xs">
            Lookup of the OCR services this app can dispatch to. Toggling
            <code> enabled </code>only does anything once the provider's HTTP
            integration has been wired in <code>api/src/lib/ocr/providers/</code>.
          </Text>
        </Stack>
        {!isSuperadmin && (
          <Text c="dimmed" size="xs">
            View-only. Superadmin can toggle providers.
          </Text>
        )}
      </Group>
      <Table withColumnBorders striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Provider</Table.Th>
            <Table.Th>Bounding boxes</Table.Th>
            <Table.Th>Capabilities</Table.Th>
            <Table.Th>Enabled</Table.Th>
            <Table.Th>Notes</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {isLoading ? (
            <Table.Tr>
              <Table.Td colSpan={5}>
                <Text c="dimmed">loading…</Text>
              </Table.Td>
            </Table.Tr>
          ) : (
            providers.map((p) => (
              <Table.Tr key={p.id}>
                <Table.Td>
                  <Stack gap={0}>
                    <Text size="sm" fw={500}>
                      {p.display_name}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {p.slug}
                    </Text>
                    {p.description && (
                      <Text size="xs" c="dimmed">
                        {p.description}
                      </Text>
                    )}
                  </Stack>
                </Table.Td>
                <Table.Td>
                  {p.supports_bounding_boxes ? (
                    <Badge color="pine" variant="light">
                      native
                    </Badge>
                  ) : (
                    <Badge color="gray" variant="light">
                      best-effort
                    </Badge>
                  )}
                </Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    {Object.entries(p.capabilities)
                      .filter(([, v]) => v)
                      .map(([k]) => (
                        <Badge key={k} variant="light" tt="lowercase">
                          {k}
                        </Badge>
                      ))}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Tooltip label={isSuperadmin ? 'Toggle enabled' : 'Superadmin only'} withArrow>
                    <Switch
                      checked={p.enabled}
                      disabled={!isSuperadmin || patch.isPending}
                      onChange={(e) => patch.mutate({ id: p.id, enabled: e.currentTarget.checked })}
                    />
                  </Tooltip>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">
                    {p.notes}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))
          )}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}
